//-- Copyright (c) 2021 TMT Insights Group, LLC
import React, { useState } from 'react';
import { Button, Grid } from '@mui/material';
import { Title, TitleBar } from '../../_common';
import IngestFileManagerModal from './IngestFileManagerModal';
import PolarisIcon from '@/components/_common/PolarisIcon';
import { FileManagerInventoryFile } from '@/redux/slices/slice_types';
import { useConfigValue } from '@/components/_hooks/useConfigValue';
import { useToast } from '@/components/_common/Toast';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { selectAllFileSources } from '@/redux/selectors/fileManagerSelectors';
import { getData } from '@/redux/slices/dataTableSlice';
import axios from '@/_utils/axios';
import { selectCanIngestFiles } from '@/redux/selectors/authSelectors';

const IngestManagementTitleBar = ({ className }: { className?: string }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<FileManagerInventoryFile[]>([]);
  const version = useConfigValue(['ingestManagementConfig', 'version']) || 'v1';
  const { success, error } = useToast();
  const fileSources = useAppSelector(selectAllFileSources);
  const dispatch = useAppDispatch();
  const hasPermissions = useAppSelector(selectCanIngestFiles);

  const handleFileSelection = (item: FileManagerInventoryFile) => {
    const alreadyAdded = selectedFiles.find((file) => file.key === item.key);
    if (alreadyAdded) {
      setSelectedFiles(selectedFiles.filter((file) => file.key !== item.key));
    } else {
      setSelectedFiles([...selectedFiles, item]);
    }
  };

  const ingestFiles = () => {
    let files = {};
    if (version === 'v2') {
      files = selectedFiles.map((file) => ({
        source_file_origin_id: fileSources.find((source: any) => source.slug === file.origin_slug)?.id,
        file_path: file.key
      }));
    } else {
      files = selectedFiles.map((file) => ({
        file_origin_id: fileSources.find((source: any) => source.slug === file.origin_slug)?.id,
        file_path: `${file.key}`
      }));
    }
    const uploadUrl = version === 'v2' ? '/ingest_manager/ingest_records/create/bulk/' : '/inventory/file_ingests/create/bulk/';

    axios
      .post(uploadUrl, { files })
      .then((res) => {
        if (!res.data.success) {
          throw new Error();
        } else {
          setSelectedFiles([]);
          setIsOpen(false);
          success('Files successfully ingested!');
        }
      })
      .catch((err) => error(err.response?.data?.message || 'There was an error ingesting files.'))
      .then(() => dispatch(getData()));
  };

  return (
    <TitleBar className={className}>
      <Grid container justifyContent="space-between" alignItems="center">
        <Title>Ingest Manager</Title>
        <Grid item>
          {hasPermissions && (
            <>
              <Button variant="contained" color="primary" onClick={() => setIsOpen(true)} startIcon={<PolarisIcon icon="add_circle" height={18} />}>
                Ingest files
              </Button>
              <IngestFileManagerModal
                open={isOpen}
                onClose={() => setIsOpen(false)}
                selectedFiles={selectedFiles}
                handleFileSelection={handleFileSelection}
                onSubmit={ingestFiles}
                resetFiles={() => setSelectedFiles([])}
              />
            </>
          )}
        </Grid>
      </Grid>
    </TitleBar>
  );
};
export default IngestManagementTitleBar;
